import type { Todo } from "@acto/taskpane/core/domain";
import { TodoLoadMoreFooter, TodoSection } from "@acto/taskpane/field-sales-2/components/patterns/TodoSection";
import { TodoRow, bucketShowsDividers, type TodoBucketVariant } from "./TodoRow";

interface TodoBucketSectionProps {
  title: string;
  todos: Todo[];
  variant: TodoBucketVariant;
  hasNextPage: boolean;
  isFetching: boolean;
  onLoadMore: () => void;
  loadMoreLabel: string;
  isCompleting: (id: string) => boolean;
  onComplete: (todo: Todo) => void;
  /** Reverts a completed todo back to open. Only meaningful for the completed bucket. */
  onUncomplete: (todo: Todo) => void;
  /** Whether a completed todo's revert is currently pending/committing. */
  isReverting: (id: string) => boolean;
  onCustomerClick: (todo: Todo) => void;
  /** Effective assignee name per todo (reflects a pending reassignment override). */
  assigneeNameFor?: (todo: Todo) => string | undefined;
  /** Opens a todo's edit sheet on row tap. Never wired for the completed bucket. */
  onRowClick?: (todo: Todo) => void;
}

/** One todo bucket: its titled glass section of rows plus a trailing in-card "Load more". */
export function TodoBucketSection({
  title,
  todos,
  variant,
  hasNextPage,
  isFetching,
  onLoadMore,
  loadMoreLabel,
  isCompleting,
  onComplete,
  onUncomplete,
  isReverting,
  onCustomerClick,
  assigneeNameFor,
  onRowClick,
}: TodoBucketSectionProps) {
  return (
    <TodoSection
      title={title}
      showDividers={bucketShowsDividers(variant)}
      footer={
        hasNextPage && <TodoLoadMoreFooter label={loadMoreLabel} isFetching={isFetching} onLoadMore={onLoadMore} />
      }
    >
      {todos.map((todo) => (
        <TodoRow
          key={todo.id}
          todo={todo}
          variant={variant}
          isCompleting={isCompleting(todo.id)}
          onComplete={variant === "completed" ? undefined : () => onComplete(todo)}
          onUncomplete={variant === "completed" ? () => onUncomplete(todo) : undefined}
          isReverting={isReverting(todo.id)}
          onCustomerClick={() => onCustomerClick(todo)}
          assigneeName={assigneeNameFor?.(todo)}
          onRowClick={variant === "completed" || !onRowClick ? undefined : () => onRowClick(todo)}
        />
      ))}
    </TodoSection>
  );
}
